import { and, desc, eq, inArray, isNotNull, isNull, sql } from "drizzle-orm";
import { unionAll } from "drizzle-orm/pg-core";
import { pg } from "~/pg.server";
import { OurLoaderArgs } from "./Loaders";
import { GameVer, lstring, tt, tturl } from "./Lang";
import { mdPages } from "drizzle/schema";
import { select } from "gamedata/GameData";
import { QuestLinkData } from "~/components/quest/QuestLink";
import { ItemLinkData } from "~/components/item/ItemLink";
import { NpcLinkData } from "~/components/npc/NpcLink";
import { SkillLinkData } from "~/components/skill/SkillLink";
import { PageLinkData } from "~/components/page/PageLink";
import { WikiLinkData } from "~/components/page/WikiLink";

export async function getImagesForPage(
  pageId: number,
  revisionImages: number[] | null
) {
  return await pg.db
    .select({ id: pg.mdImages.id, path: pg.mdImages.path })
    .from(pg.mdImages)
    .where(
      and(
        eq(pg.mdImages.pageId, pageId),
        inArray(pg.mdImages.id, revisionImages ?? [])
      )
    );
}

export async function getContributorsForPage(pageId: number) {
  return await pg.db
    .select({
      id: pg.users.id,
      name: pg.users.login,
      avatar: pg.users.avatar,
    })
    .from(pg.mdRevisions)
    .innerJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(
      and(
        eq(pg.mdRevisions.pageId, pageId),
        eq(pg.mdRevisions.isDeleted, false)
      )
    )
    .groupBy(pg.users.id);
}

//feed
export type ActivityAction = "created" | "updated";
export type ActivityType =
  | "quest_page"
  | "npc_page"
  | "item_page"
  | "user_page"
  | "wiki_page";

export function latestActivityQuery() {
  const questPagesQuery = pg.db
    .select({
      id: pg.mdPages.id,
      created_at: pg.mdRevisions.createdAt,
      user: {
        id: pg.users.id,
        name: pg.users.login,
        avatar: pg.users.avatar,
      },
      game: pg.mdPages.game,
      type: sql<ActivityType>`'quest_page'`.as("type"),
      objId: sql<number | null>`${mdPages.questId}`,
      objUname: sql<string | null>`${null}`,
      action: sql<ActivityAction>`CASE 
        WHEN ${pg.mdRevisions.prevRevisionId} IS NULL THEN 'created'
        ELSE 'updated'
      END`.as("action"),
      title: { en: pg.mdRevisions.titleEn, ru: pg.mdRevisions.titleRu },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.id, pg.mdRevisions.pageId))
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(and(isNotNull(pg.mdPages.questId), eq(pg.mdPages.isDeleted, false)));

  const npcPagesQuery = pg.db
    .select({
      id: pg.mdPages.id,
      created_at: pg.mdRevisions.createdAt,
      user: {
        id: pg.users.id,
        name: pg.users.login,
        avatar: pg.users.avatar,
      },
      game: pg.mdPages.game,
      type: sql<ActivityType>`'npc_page'`.as("type"),
      objId: sql<number | null>`${null}`,
      objUname: sql<string | null>`${mdPages.npcName}`,
      action: sql<ActivityAction>`CASE 
        WHEN ${pg.mdRevisions.prevRevisionId} IS NULL THEN 'created'
        ELSE 'updated'
      END`.as("action"),
      title: { en: pg.mdRevisions.titleEn, ru: pg.mdRevisions.titleRu },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.id, pg.mdRevisions.pageId))
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(and(isNotNull(pg.mdPages.npcName), eq(pg.mdPages.isDeleted, false)));

  const itemPagesQuery = pg.db
    .select({
      id: pg.mdPages.id,
      created_at: pg.mdRevisions.createdAt,
      user: {
        id: pg.users.id,
        name: pg.users.login,
        avatar: pg.users.avatar,
      },
      game: pg.mdPages.game,
      type: sql<ActivityType>`'item_page'`.as("type"),
      objId: sql<number | null>`${null}`,
      objUname: sql<string | null>`${mdPages.itemName}`,
      action: sql<ActivityAction>`CASE 
        WHEN ${pg.mdRevisions.prevRevisionId} IS NULL THEN 'created'
        ELSE 'updated'
      END`.as("action"),
      title: { en: pg.mdRevisions.titleEn, ru: pg.mdRevisions.titleRu },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.id, pg.mdRevisions.pageId))
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(
      and(isNotNull(pg.mdPages.itemName), eq(pg.mdPages.isDeleted, false))
    );

  const userPagesQuery = pg.db
    .select({
      id: pg.mdPages.id,
      created_at: pg.mdRevisions.createdAt,
      user: { id: pg.users.id, name: pg.users.login, avatar: pg.users.avatar },
      game: pg.mdPages.game,
      type: sql<ActivityType>`'user_page'`.as("type"),
      objId: sql<number | null>`${mdPages.draftId}`,
      objUname: sql<string | null>`${null}`,
      action: sql<ActivityAction>`CASE 
          WHEN ${pg.mdRevisions.prevRevisionId} IS NULL THEN 'created'
          ELSE 'updated'
        END`.as("action"),
      title: { en: pg.mdRevisions.titleEn, ru: pg.mdRevisions.titleRu },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.revisionId, pg.mdRevisions.id))
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .leftJoin(pg.userPages, eq(pg.mdPages.draftId, pg.userPages.id))
    .where(
      and(
        isNotNull(pg.mdPages.draftId),
        eq(pg.userPages.public, true),
        eq(pg.mdPages.isDeleted, false)
      )
    );

  const wikiPagesQuery = pg.db
    .select({
      id: pg.mdPages.id,
      created_at: pg.mdRevisions.createdAt,
      user: { id: pg.users.id, name: pg.users.login, avatar: pg.users.avatar },
      game: pg.mdPages.game,
      type: sql<ActivityType>`'wiki_page'`.as("type"),
      objId: sql<number | null>`${mdPages.draftId}`,
      objUname: sql<string | null>`${null}`,
      action: sql<ActivityAction>`CASE 
          WHEN ${pg.mdRevisions.prevRevisionId} IS NULL THEN 'created'
          ELSE 'updated'
        END`.as("action"),
      title: { en: pg.mdRevisions.titleEn, ru: pg.mdRevisions.titleRu },
    })
    .from(pg.mdPages)
    .innerJoin(pg.mdRevisions, eq(pg.mdPages.id, pg.mdRevisions.pageId))
    .leftJoin(pg.users, eq(pg.mdRevisions.userId, pg.users.id))
    .where(
      and(
        eq(pg.mdPages.isDeleted, false),
        isNull(pg.mdPages.npcName),
        isNull(pg.mdPages.itemName),
        isNull(pg.mdPages.locName),
        isNull(pg.mdPages.questId),
        isNull(pg.mdPages.skillId),
        isNull(pg.mdPages.draftId)
      )
    );

  return unionAll(
    questPagesQuery,
    userPagesQuery,
    npcPagesQuery,
    itemPagesQuery,
    wikiPagesQuery
  )
    .orderBy(desc(sql`created_at`))
    .limit(40);
}

export type LatestActivityQuery = Awaited<
  ReturnType<typeof latestActivityQuery>
>[0];

export type LatestActivityQueryWithObject =
  | (LatestActivityQuery & { _: "quest"; linkData: QuestLinkData })
  | (LatestActivityQuery & { _: "item"; linkData: ItemLinkData })
  | (LatestActivityQuery & { _: "npc"; linkData: NpcLinkData })
  | (LatestActivityQuery & { _: "skill"; linkData: SkillLinkData })
  | (LatestActivityQuery & { _: "user"; linkData: PageLinkData })
  | (LatestActivityQuery & { _: "wiki"; linkData: WikiLinkData });

export function pagesWithObjects(props: {
  activities: LatestActivityQuery[];
  args: OurLoaderArgs;
}): LatestActivityQueryWithObject[] {
  const { activities, args } = props;
  const { realm } = args;
  const t = tt.bind(null, realm.lang);

  return activities
    .map((activity) => {
      const gver = (activity.game?.[0] ?? "gf") as GameVer;
      if (activity.type === "quest_page") {
        if (!activity.objId) {
          return null;
        }

        const quest = args.gd.all_gd?.[gver].quests.get(activity.objId);
        if (quest) {
          const data = select(quest, { id: true, uname: true, name: t });
          return {
            _: "quest" as const,
            linkData: {
              id: data.id,
              uname: quest.uname,
              name: data.name,
              gver,
            },
            ...activity,
          };
        } else {
          return null;
        }
      }
      if (activity.type === "npc_page") {
        if (!activity.objUname) {
          return null;
        }

        const npc = args.gd.all_gd?.[gver].npcs.get(activity.objUname!);
        if (npc) {
          const data = select(npc, {
            npcName: true,
            id: true,
            level: true,
            nick: t,
            nickColor: true,
            name: t,
            race: { icon: true },
          });
          return {
            _: "npc" as const,
            linkData: {
              npcName: data.npcName,
              name: data.name,
              level: data.level,
              nick: data.nick,
              nickColor: data.nickColor,
              race: data.race,
              gver,
            },
            ...activity,
          };
        } else {
          return null;
        }
      }
      if (activity.type === "item_page") {
        if (!activity.objUname) {
          return null;
        }
        const item = args.gd.all_gd?.[gver].items.get(activity.objUname!);

        if (item) {
          const data = select(item, {
            itemName: true,
            addName: t,
            id: true,
            name: t,
            icon: true,
            crystalType: true,
          });
          return {
            _: "item" as const,
            linkData: {
              itemName: data.itemName,
              name: data.name,
              addName: data.addName,
              icon: data.icon,
              crystalType: data.crystalType,
              gver,
            },
            ...activity,
          };
        } else {
          return null;
        }
      }
      if (activity.type === "user_page") {
        return {
          _: "user" as const,
          linkData: {
            id: activity.id,
            userId: activity.user?.id!,
            name: t({
              en: activity.title.en ?? "Default Title",
              ru: activity.title.ru ?? "Без заголовка",
            }),
            gver,
          },
          ...activity,
        };
      }
      if (activity.type === "wiki_page") {
        return {
          _: "wiki" as const,
          linkData: {
            id: activity.id,
            name: t({
              en: activity.title.en ?? "Default Title",
              ru: activity.title.ru ?? "Без заголовка",
            }),
            gver,
          },
          ...activity,
        };
      } else {
        return null;
      }
    })
    .filter((activity) => activity !== null);
}

type ActionText = {
  [key in ActivityType]: {
    created: lstring;
    updated: lstring;
  };
};

const activityTextMap: ActionText = {
  quest_page: {
    created: {
      en: "added a description for",
      ru: "добавил описание для",
    },
    updated: {
      en: "updated the description for",
      ru: "обновил описание для",
    },
  },
  npc_page: {
    created: {
      en: "added a description for NPC",
      ru: "добавил описание для НПС",
    },
    updated: {
      en: "updated the description for NPC",
      ru: "обновил описание для НПС",
    },
  },
  item_page: {
    created: {
      en: "added a description for item",
      ru: "добавил описание для предмета",
    },
    updated: {
      en: "updated the description for item",
      ru: "обновил описание для предмета",
    },
  },
  wiki_page: {
    created: { en: "created wiki page", ru: "создал страницу" },
    updated: { en: "updated wiki page", ru: "обновил страницу" },
  },
  user_page: {
    created: { en: "created their page", ru: "создал страницу" },
    updated: { en: "updated their page", ru: "обновил страницу" },
  },
};

export function getActivityText(
  type: ActivityType,
  action: ActivityAction,
  lang: "en" | "ru"
): string {
  const mapping = activityTextMap[type]?.[action];
  if (!mapping) return "";
  return mapping[lang];
}
